import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { badgeColors } from 'constants/colors'
import { IConfigStateGroups, ITag } from 'constants/interfaces'

export const USER_GROUP_ROLES = {
  WORKER: 0,
  MANAGER: 1,
  MANAGER_AVATAR: 2,
}

export const USER_PERMISSIONS = {
  VIEW: 1,
  EDIT: 2,
  APP_ADMIN: 3,
}

export const getRoleBadgeProps = (role?: number) => {
  switch (role) {
    case USER_GROUP_ROLES.WORKER:
      return badgeColors.green
    case USER_GROUP_ROLES.MANAGER:
      return badgeColors.blue
    case USER_GROUP_ROLES.MANAGER_AVATAR:
      return badgeColors.violet
    default:
      return {
        text: 'gray.600',
        background: 'gray.100',
        border: 'gray.300',
      }
  }
}

export const getRoleName = (role?: number) => {
  switch (role) {
    case USER_GROUP_ROLES.WORKER:
      return 'Worker'
    case USER_GROUP_ROLES.MANAGER:
      return 'Manager'
    case USER_GROUP_ROLES.MANAGER_AVATAR:
      return 'Manager Avatar'
    default:
      return ''
  }
}

const PERMISSION_NAMES: { [key: number]: string } = {
  [-1]: 'Unauthorized',
  [0]: 'View',
  [1]: 'View',
  [2]: 'Edit',
  [3]: 'App Admin',
}

const formatServicesPermissions = (perms?: { [key: string]: number }) => {
  if (!perms) return ''
  return Object.entries(perms)
    .map(([key, val]) => `${key}:${PERMISSION_NAMES[val] ?? val}`)
    .join(';')
}

export const exportUsersToXLSX = async (users: any[], groups: IConfigStateGroups, tags: ITag[]) => {
  const tagBySk: { [sk: string]: ITag } = {}
  tags.forEach((t) => {
    tagBySk[t.sk] = t
  })

  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('Users')

  worksheet.columns = [
    { header: 'uid', key: 'uid', width: 30 },
    { header: 'profile_img_url', key: 'profile_img_url', width: 40 },
    { header: 'first_name', key: 'first_name', width: 20 },
    { header: 'last_name', key: 'last_name', width: 20 },
    { header: 'sec_uid', key: 'sec_uid', width: 20 },
    { header: 'description', key: 'description', width: 30 },
    { header: 'language', key: 'language', width: 12 },
    { header: 'email', key: 'email', width: 30 },
    { header: 'country_code', key: 'country_code', width: 14 },
    { header: 'phone_number', key: 'phone_number', width: 18 },
    { header: 'enable_mfa', key: 'enable_mfa', width: 12 },
    { header: 'role', key: 'role', width: 16 },
    { header: 'permission', key: 'permission', width: 16 },
    { header: 'services_permissions', key: 'services_permissions', width: 40 },
    { header: 'group_names', key: 'group_names', width: 40 },
    { header: 'tag_names', key: 'tag_names', width: 40 },
  ]

  worksheet.getRow(1).font = { bold: true }
  worksheet.getRow(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' },
  }

  users.forEach((u) => {
    const groupIds: string[] = u.groups ?? []
    const groupNames = groupIds.map((gid) => groups[gid]?.name ?? gid)
    const tagNames = (u.tags ?? []).map((sk: string) => tagBySk[sk]?.name ?? sk)

    worksheet.addRow({
      uid: u.uid ?? '',
      profile_img_url: u.profile_img_url ?? '',
      first_name: u.first_name ?? '',
      last_name: u.last_name ?? '',
      sec_uid: u.sec_uid ?? '',
      description: u.desc ?? '',
      language: u.locale ?? '',
      email: u.email ?? '',
      country_code: u.country_code ?? '',
      phone_number: u.phone_number ?? '',
      enable_mfa: u.enable_mfa ? 'true' : 'false',
      role: getRoleName(u.user_group_role),
      permission: PERMISSION_NAMES[u.permission] ?? '',
      services_permissions: formatServicesPermissions(u.services_permissions),
      group_names: groupNames.join(';'),
      tag_names: tagNames.join(';'),
    })
  })

  const buffer = await workbook.xlsx.writeBuffer()
  const date = new Date().toISOString().split('T')[0]
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  saveAs(blob, `users_export_${date}.xlsx`)
}
